import { useState } from "react";
import { Icon } from "../lib/Icon";
import { usePortal } from "./state";
import { pathsFor } from "./reading";
import { SourceBadge } from "./components";

// Progress is per person and survives a brokerage change (DATA_MODEL.md §3).
// The three "own"-org paths are tenant-authored; the rest are platform.
const TENANT_PATHS = new Set(["New MLS staff member", "New broker admin", "New member first week"]);

export function PortalOnboarding() {
  const { brand, brandId, user } = usePortal();
  const paths = pathsFor(brandId);
  // Which paths the reader has started this session (keeps them on the path — the
  // CTA advances into it rather than navigating away).
  const [started, setStarted] = useState<Record<string, boolean>>({});

  return (
    <div style={{ maxWidth: 1060, margin: "0 auto", padding: "30px 28px 64px", display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px", color: "#1d241f" }}>Onboarding paths</h1>
        <p style={{ margin: "6px 0 0", fontSize: 13.5, color: "#8a8e88", textWrap: "pretty" }}>Guided sequences of reading, video and hands-on steps. Your progress is yours — it follows you if you change brokerage.</p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {paths.map((p) => {
          const pct = Math.round((p.done / p.steps) * 100);
          const full = p.done === p.steps;
          const tenant = TENANT_PATHS.has(p.name);
          return (
            <div key={p.name} style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "18px 18px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <SourceBadge source={{ label: tenant ? user.org : brand.name, tenant }} brand={brand} />
                    <span style={{ fontSize: 12, color: "#8a8e88" }}>{p.who}</span>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: "#1d241f" }}>{p.name}</div>
                  <div style={{ fontSize: 13, color: "#4a504a", marginTop: 4, textWrap: "pretty" }}>{p.blurb}</div>
                </div>
                <div style={{ textAlign: "right", flex: "none" }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: full ? "#1f8a5b" : "#8a8e88" }}>{full ? "Complete" : `${p.done}/${p.steps}`}</div>
                  <div style={{ fontSize: 11.5, color: "#8a8e88", marginTop: 2 }}>{p.mins} min</div>
                </div>
              </div>

              <div style={{ height: 4, borderRadius: 2, background: "#f0efe9", margin: "14px 0" }}>
                <div style={{ width: `${pct}%`, height: "100%", borderRadius: 2, background: full ? "#1f8a5b" : brand.ink }} />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {p.stepNames.map((name, i) => {
                  const done = i < p.done;
                  const isNext = i === p.done;
                  const kind = i % 3 === 1 ? "video" : i % 4 === 3 ? "task" : "article";
                  const icon = kind === "video" ? "circle-play" : kind === "task" ? "square-check-big" : "file-text";
                  const meta = kind === "video" ? "Watch · 6 min" : kind === "task" ? "Do it in your account" : "Read · 5 min";
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 11, padding: "9px 11px", borderRadius: 7, background: isNext ? brand.soft : "transparent" }}>
                      <Icon name={done ? "check-circle" : icon} size={16} color={done ? "#1f8a5b" : isNext ? brand.ink : "#bcbfba"} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: isNext ? 600 : 500, color: done ? "#8a8e88" : "#1d241f", textDecoration: done ? "line-through" : "none" }}>{name}</span>
                      <span style={{ fontSize: 11.5, color: "#8a8e88", flex: "none" }}>{meta}</span>
                    </div>
                  );
                })}
              </div>

              <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <button
                  onClick={() => setStarted((s) => ({ ...s, [p.name]: true }))}
                  style={{ border: 0, background: full ? "#f0efe9" : brand.ink, color: full ? "#4a504a" : "#fff", borderRadius: 3, padding: "10px 18px", fontSize: 12, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", cursor: "pointer" }}
                >
                  {full ? "Review path" : started[p.name] || p.done ? "Resume" : "Start path"}
                </button>
                {started[p.name] && !full && p.stepNames[p.done] && (
                  <span style={{ fontSize: 12.5, color: "#4a504a", display: "flex", alignItems: "center", gap: 7 }}>
                    <Icon name="arrow-up-right" size={15} color={brand.ink} />
                    Next up: <strong style={{ fontWeight: 600, color: "#1d241f" }}>{p.stepNames[p.done]}</strong>
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
